import Carousel from '@carvana/ds-carousel';
import { icon } from '@carvana/ds-styles';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { ChevronRight } from 'lucide-react';
import { useContext } from 'react';
import TrackWrapper from '~/components/common/TrackWrapper';
import bodyStylesList from '~/constants/common/bodystylesList';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import { BodyStyle } from '~/contracts/common';
import useGetConsumerHref from '~/hooks/common/selectors/useGetConsumerHref';
import formatEvent from '~/utilities/formatEvent';
import generateSearchLinkFilter from '~/utilities/generateSearchFilterLink';

function Header() {
  return (
    <div className="flex w-full justify-between px-2xl sm:items-end sm:justify-start sm:gap-xl sm:px-3xl sm:pb-sm md:px-0 md:pl-[84px]">
      <h4 className="text-text-inverse t-heading-lg md:t-heading-2xl">
        Shop with Shaq
      </h4>

      <a
        href="/cars"
        data-analytics-id="Shaq’s Favorites"
        data-analytics-attributes={JSON.stringify({
          text: 'Shop all'
        })}
        className="h-fit"
        data-testid="shop-all"
      >
        <div className="flex items-center gap-md sm:items-end">
          <p className="text-text-inverse t-label-md">Shop all</p>
          <ChevronRight color={icon.inverse} />
        </div>
      </a>
    </div>
  );
}

function BodyStyleCard({ bodyStyle }: { bodyStyle: BodyStyle }) {
  const getHref = useGetConsumerHref();
  const searchLink = generateSearchLinkFilter({
    filters: {
      ...(bodyStyle.bodyStyleInCvnaid && {
        bodyStyles: [bodyStyle.bodyStyleInCvnaid.toString()]
      }),
      ...(bodyStyle.fuelTypeInCvnaid && {
        fuelTypes: [bodyStyle.fuelTypeInCvnaid.toString()]
      })
    }
  });

  return (
    <a
      href={getHref(searchLink)}
      key={bodyStyle.alt}
      data-analytics-id="Shaq’s Favorites"
      data-analytics-attributes={JSON.stringify({
        description: 'Popular body style tile',
        text: bodyStyle.alt
      })}
      className="inline-block"
      data-testid="body-style-card"
    >
      <div className="group flex h-full min-w-[177px] flex-col items-center gap-lg p-lg text-center text-text-inverse transition-all duration-200 ease-in t-body-sm hover:t-strong-sm">
        <picture className="block h-full w-full transition-transform duration-200 ease-in-out group-hover:scale-110">
          <source media="(min-width: 1024px)" srcSet={bodyStyle.image} />
          <source media="(min-width: 768px)" srcSet={bodyStyle.image} />
          <img
            src={`${bodyStyle.image}?width=153px`}
            alt={bodyStyle.alt}
            className="h-full w-full object-cover"
          />
        </picture>
        <div className="truncate">{bodyStyle.alt}</div>
      </div>
    </a>
  );
}

export default function ShaqsFavorites() {
  const { trackEvent } = useContext(AnalyticsContext);

  return (
    <TrackWrapper
      event={formatEvent('Shaq’s Favorites', {
        action: 'viewed'
      })}
      className="pt-4xl md:pt-6xl"
    >
      <Carousel
        ariaLabel="Shaqs favorite vehicles"
        classes={{
          arrows:
            'sm:pr-3xl md:pr-[84px] [&_button:not(:disabled)_svg_path]:!fill-icon-primary [&_button:not(:disabled)]:!border-2 [&_button:not(:disabled)]:!border-border-primary',
          topSection: 'w-full gap-0',
          slide:
            'first:pl-2xl last:pr-2xl md:first:pl-[84px] md:last:pr-2xl lg:md:last:pr-0 xl:last:pr-[84px]',
          slides: 'gap-0 sm:gap-3xl md:gap-4xl'
        }}
        headerContent={<Header />}
        hideScrollBar
        slideByVisible
        trackEvent={event => {
          trackEvent('Shaq’s Favorites', {
            ...event,
            action: INTERACTION_TO_ACTION[event.interactionType]
          });
        }}
      >
        {bodyStylesList.map(bodyStyle => {
          return <BodyStyleCard key={bodyStyle.alt} bodyStyle={bodyStyle} />;
        })}
      </Carousel>
    </TrackWrapper>
  );
}
